import useFiveYearGrowth from '@hooks/useFiveYearGrowth'
import SuburbProfileFilters from '../../SuburbProfileFilters'
import * as S from '../SalesComparison.styled'
import React from 'react'

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-au', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value)
}

const formatWithoutRounding = (num, decimals = 2) => {
  if (!num) return ''
  const numStr = num.toString()
  const decimalIndex = numStr.indexOf('.')
  if (decimalIndex === -1) return num.toFixed(decimals)
  const truncated = numStr.substring(0, decimalIndex + decimals + 1)
  // Pad with zeros if needed
  const parts = truncated.split('.')
  if (parts[1] && parts[1].length < decimals) {
    return parts[0] + '.' + parts[1].padEnd(decimals, '0')
  }
  return truncated
}

export const hasValidFiveYearGrowthItem = (data) => {
  return data.some(item =>
    item.properties_sold ||
    item.median_sale_price ||
    item.sales_growth_year_over_year ||
    item.properties_leased ||
    item.median_rent ||
    item.rental_yield
  );
};

const FiveYearGrowth = ({
  fiveYearGrowth: initialFiveYearGrowth,
  suburb,
  slug,
  availableBedrooms,
}) => {
  const [type, setType] = React.useState('all')
  const [bedrooms, setBedrooms] = React.useState('Overall')
  const { fiveYearGrowth } = useFiveYearGrowth(
    slug,
    type,
    bedrooms,
    initialFiveYearGrowth
  )

  if ((!fiveYearGrowth || fiveYearGrowth.length === 0) || !hasValidFiveYearGrowthItem(fiveYearGrowth || [])) {
    return null
  }

  return (
    <S.Group>
      <S.GroupHeading>
        <S.Title>{suburb.toUpperCase()} MARKET INSIGHTS</S.Title>
        <SuburbProfileFilters
          bedrooms={bedrooms}
          availableBedrooms={availableBedrooms}
          onBedroomsChange={setBedrooms}
          type={type}
          onTypeChange={setType}
          showCommercial={false}
        />
      </S.GroupHeading>
      <S.TableResonsive className='hidden-mobile'>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Properties Sold</th>
              <th>
                Median Sale Price
                <br />
                (All Residential)
              </th>
              <th>Sales Growth YoY</th>
              <th>Properties Leased</th>
              <th>
                Median Rental Price
                <br />
                (All Residential)
              </th>
              <th>Rental Yield</th>
            </tr>
          </thead>
          <tbody>
            {fiveYearGrowth.map((it) => (
              <tr key={it.year}>
                <td>{it.year}</td>
                <td>{Math.floor(it.properties_sold)}</td>
                <td>
                  {it.median_sale_price
                    ? formatCurrency(it.median_sale_price)
                    : '-'}
                </td>
                <td>
                  {it.sales_growth_year_over_year
                    ? `${formatWithoutRounding(
                      it.sales_growth_year_over_year
                    )}%`
                    : 'no change'}
                </td>
                <td>{Math.floor(it.properties_leased)}</td>
                <td>{formatCurrency(it.median_rent)}pw</td>
                <td>{formatWithoutRounding(it.rental_yield)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </S.TableResonsive>
      <S.TableResonsive className='visible-mobile'>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Sold</th>
              <th>
                Median Sale Price
                <br />
                (All Residential)
              </th>
              <th>Sales Growth YoY</th>
            </tr>
          </thead>
          <tbody>
            {fiveYearGrowth.map((it) => (
              <tr key={it.year}>
                <td>{it.year}</td>
                <td>{Math.floor(it.properties_sold)}</td>
                <td>
                  {it.median_sale_price
                    ? formatCurrency(it.median_sale_price)
                    : '-'}
                </td>
                <td>
                  <span>
                    {it.sales_growth_year_over_year
                      ? `${it.sales_growth_year_over_year > 0 ? '+' : ''
                      }${formatWithoutRounding(
                        it.sales_growth_year_over_year
                      )}%`
                      : 'no change'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </S.TableResonsive>
      <S.Divider />
      <S.TableResonsive className='visible-mobile'>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Leased</th>
              <th>
                Median Rental Price
                <br />
                (All Residential)
              </th>
              <th>Rental Yield</th>
            </tr>
          </thead>
          <tbody>
            {fiveYearGrowth.map((it) => (
              <tr key={it.year}>
                <td>{it.year}</td>
                <td>{Math.floor(it.properties_leased)}</td>
                <td>{formatCurrency(it.median_rent)}pw</td>
                <td>{formatWithoutRounding(it.rental_yield)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </S.TableResonsive>
    </S.Group>
  )
}

export default FiveYearGrowth

